import { zodResolver } from "@hookform/resolvers/zod";
import { Autocomplete, Box, Button, Grid, Stack, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import api from "../../../api";
import CustomMDEditor from "../../../components/CustomMD/CustomMDEditor";
import { useLogin } from "../../../context/Login/LoginContext";
import { getBackendUrl } from "../../../util/envFn";
import { categoryMap, JobCategory, JobFormInputs, jobFormSchema, JobType, jobTypeMap } from "../AdminPanel";
import { JobPost } from "../types";
import AdminPanelJobPostCard from "./AdminPanelJobPostCard";

const AdminPanelManageJobPostCard = () => {
    const [jobPosts, setJobPosts] = useState<JobPost[]>([]);
    const { user } = useLogin();
    const { enqueueSnackbar } = useSnackbar();

    const { control, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<JobFormInputs>({
        resolver: zodResolver(jobFormSchema),
        defaultValues: {
            jobTitle: '',
            pmEmail: user?.email || '',
            jobDescription: '',
            location: '',
            category: [],
            type: [],
            salary: '',
            company: ''
        }
    });

    const fetchJobs = () => {
        api.get(getBackendUrl("jobs"))
            .then(res => setJobPosts(res.data.sort((a: JobPost, b: JobPost) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())))
            .catch(err => enqueueSnackbar("Failed to fetch jobs", { variant: "error" }));
    }

    const onSubmit = async (data: JobFormInputs) => {
        try {
            await api.post(getBackendUrl("jobs"), {
                title: data.jobTitle,
                description: data.jobDescription,
                pmEmail: data.pmEmail,
                location: data.location,
                category: data.category.join(","),
                type: data.type.join(","),
                salary: data.salary,
                company: data.company
            }).then(res => {
                fetchJobs();
                enqueueSnackbar("Job added successfully", { variant: "success" });
            })
                .catch(err => enqueueSnackbar("Failed to add job", { variant: "error" }));

            reset();
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    useEffect(() => {
        const formValues = watch();
        if (user) {
            reset({
                jobTitle: formValues.jobTitle,
                pmEmail: user.email,
                jobDescription: formValues.jobDescription
            });
        }
    }, [user, reset, watch]);

    return (
        <Stack sx={{
            mb: 4,
            gap: 2,
            bgcolor: 'background.paper',
        }}>
            <Stack p={3} gap={2}>
                <Typography variant="h6" component="h2" >
                    Manage Job Posts
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                            <Controller
                                name="jobTitle"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="Job Title"
                                        error={!!errors.jobTitle}
                                        helperText={errors.jobTitle?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="company"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="Company"
                                        error={!!errors.company}
                                        helperText={errors.company?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="pmEmail"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="PM Email"
                                        type="email"
                                        error={!!errors.pmEmail}
                                        helperText={errors.pmEmail?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="location"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="Location"
                                        error={!!errors.location}
                                        helperText={errors.location?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="category"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        multiple
                                        options={Object.keys(categoryMap)}
                                        getOptionLabel={(option) => categoryMap[option as JobCategory]}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                label="Category"
                                                error={!!errors.category}
                                                helperText={errors.category?.message}
                                            />
                                        )}
                                        onChange={(_, value) => field.onChange(value || [])}
                                        value={field.value || []}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="type"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        multiple
                                        options={Object.keys(jobTypeMap)}
                                        getOptionLabel={(option) => jobTypeMap[option as JobType]}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                label="Type"
                                                error={!!errors.type}
                                                helperText={errors.type?.message}
                                            />
                                        )}
                                        onChange={(_, value) => field.onChange(value || [])}
                                        value={field.value || []}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="salary"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        label="Salary"
                                        error={!!errors.salary}
                                        helperText={errors.salary?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="jobDescription"
                                control={control}
                                render={({ field }) => (
                                    <CustomMDEditor
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={errors.jobDescription?.message}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        type="submit"
                        disabled={isSubmitting}
                        sx={{ borderRadius: 1 }}
                    >
                        {isSubmitting ? 'Adding...' : 'Add Job'}
                    </Button>
                </form>

                <Stack gap={1} mt={4}>
                    <Typography variant="h5" component="h2" mb={2}>
                        Job Posts
                    </Typography>
                    {jobPosts.map((job, index) => <AdminPanelJobPostCard key={index} job={job} index={index} fetchJobs={fetchJobs} />)}
                </Stack>
            </Stack>
        </Stack>
    )
}

export default AdminPanelManageJobPostCard;
